import { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { API } from "@/App";
import Sidebar from "@/components/Sidebar";
import LeadModal from "@/components/LeadModal";
import ConfirmDialog from "@/components/ConfirmDialog";
import MultiSelectDropdown from "@/components/MultiSelectDropdown";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { 
  Plus, 
  Search, 
  MoreHorizontal, 
  Edit, 
  Trash2, 
  Phone,
  PhoneOutgoing,
  MessageSquare,
  MessageCircle,
  Calendar,
  UserPlus,
  Download,
  Upload,
  FileSpreadsheet,
  X,
  Users
} from "lucide-react";

const Leads = ({ user, setUser }) => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState([]);
  const [uploadFilter, setUploadFilter] = useState("all");
  const [teamMemberFilter, setTeamMemberFilter] = useState("all");
  const [categoryFilter, setCategoryFilter] = useState([]);
  // Phone presence filter — multi-select. Values map to backend phone_filter
  // CSV: mobile_has / mobile_blank / phone_has / phone_blank.
  const [phoneFilter, setPhoneFilter] = useState([]);
  const [uploads, setUploads] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedLead, setSelectedLead] = useState(null);
  const [initialModalTab, setInitialModalTab] = useState(null);
  const [salesReps, setSalesReps] = useState([]);
  const [isImportModalOpen, setIsImportModalOpen] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [importLoading, setImportLoading] = useState(false);
  
  // Bulk selection state
  const [selectedLeadIds, setSelectedLeadIds] = useState([]);
  const [lastSelectedIndex, setLastSelectedIndex] = useState(null);
  const [isAssignModalOpen, setIsAssignModalOpen] = useState(false);
  // Multi-assign: array of user IDs. Empty = nothing selected.
  const [bulkAssignUserIds, setBulkAssignUserIds] = useState([]);
  
  // Confirmation dialog state
  const [deleteConfirm, setDeleteConfirm] = useState({ open: false, leadId: null });
  const [bulkDeleteConfirm, setBulkDeleteConfirm] = useState(false);
  
  const token = localStorage.getItem("token");

  const statuses = [
    "New", "Attempted Contact", "Contacted", "Not Yet", "Interested", "Not Interested", 
    "Meeting Scheduled", "Proposal Sent", "Won", "Lost", "Invalid Details"
  ];
  
  const categories = [
    "Architectural", "Residential", "Renovations", "Project Homes", "Commercial"
  ];

  // Phone-presence filter options — surfaces leads by whether they have a
  // mobile / phone number on file. Multiple selected = OR (e.g. "Has Mobile"
  // + "Phone Blank" returns leads matching either).
  const phoneOptions = [
    { value: "mobile_has", label: "Has Mobile" },
    { value: "mobile_blank", label: "Mobile Blank" },
    { value: "phone_has", label: "Has Phone" },
    { value: "phone_blank", label: "Phone Blank" },
  ];

  const statusColors = {
    "New": "bg-slate-100 text-slate-700",
    "Attempted Contact": "bg-amber-100 text-amber-700",
    "Contacted": "bg-blue-100 text-blue-700",
    "Not Yet": "bg-orange-100 text-orange-700",
    "Interested": "bg-emerald-100 text-emerald-700",
    "Not Interested": "bg-red-100 text-red-700",
    "Meeting Scheduled": "bg-purple-100 text-purple-700",
    "Proposal Sent": "bg-indigo-100 text-indigo-700",
    "Won": "bg-emerald-200 text-emerald-800",
    "Lost": "bg-red-200 text-red-800",
    "Invalid Details": "bg-gray-200 text-gray-700"
  };

  useEffect(() => {
    fetchLeads();
    if (user?.role === "manager") {
      fetchSalesReps();
      fetchUploads();
    }
  }, [search, statusFilter, categoryFilter, uploadFilter, teamMemberFilter, phoneFilter]);

  const openRequest = useRef(0);
  const [leadSaveError, setLeadSaveError] = useState("");
  // Each entry point loads the saved record, never the list's cached snapshot.
  const fetchAndOpenLead = useCallback(async (leadId) => {
    if (!leadId) return;
    const requestId = ++openRequest.current;
    try {
      const response = await axios.get(`${API}/leads/${leadId}`, {
        withCredentials: true,
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      });
      if (requestId !== openRequest.current) return;
      setLeadSaveError("");
      setSelectedLead(response.data);
      setIsModalOpen(true);
    } catch (error) {
      if (requestId !== openRequest.current) return;
      toast.error("Could not open the lead. It may have been removed or reassigned.");
    }
  }, [token]);

  useEffect(() => {
    const openLeadId = searchParams.get("open");
    if (!openLeadId) return;
    setInitialModalTab(searchParams.get("tab") || null);
    setSearchParams({}, { replace: true });
    fetchAndOpenLead(openLeadId);
  }, [searchParams, setSearchParams, fetchAndOpenLead]);

  useEffect(() => () => { ++openRequest.current; }, []);

  // Clear selection when leads change
  useEffect(() => {
    setSelectedLeadIds([]);
  }, [leads]);

  const fetchLeads = async () => {
    try {
      const params = new URLSearchParams();
      if (search) params.append("search", search);
      if (statusFilter && statusFilter.length > 0) params.append("status", statusFilter.join(","));
      if (categoryFilter && categoryFilter.length > 0) params.append("category", categoryFilter.join(","));
      if (uploadFilter && uploadFilter !== "all" && user?.role === "manager") params.append("upload_id", uploadFilter);
      if (teamMemberFilter && teamMemberFilter !== "all" && user?.role === "manager") params.append("assigned_to", teamMemberFilter);
      if (phoneFilter && phoneFilter.length > 0) params.append("phone_filter", phoneFilter.join(","));
      
      const response = await axios.get(`${API}/leads?${params.toString()}`, {
        withCredentials: true,
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      });
      setLeads(response.data);
    } catch (error) {
      console.error("Failed to fetch leads:", error);
      toast.error("Failed to load leads");
    } finally {
      setLoading(false);
    }
  };

  const fetchUploads = async () => {
    try {
      const response = await axios.get(`${API}/uploads`, {
        withCredentials: true,
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      });
      setUploads(response.data);
    } catch (error) {
      console.error("Failed to fetch uploads:", error);
    }
  };

  const fetchSalesReps = async () => {
    try {
      const response = await axios.get(`${API}/users/sales-reps`, {
        withCredentials: true,
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      });
      setSalesReps(response.data);
    } catch (error) {
      console.error("Failed to fetch sales reps:", error);
    }
  };

  const handleLogout = async () => {
    try {
      await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
    } catch (e) {
      console.error("Logout error:", e);
    }
    localStorage.removeItem("token");
    navigate("/");
  };

  const handleCreateLead = () => {
    ++openRequest.current;
    setLeadSaveError("");
    setSelectedLead(null);
    setIsModalOpen(true);
  };

  const handleEditLead = (lead) => {
    fetchAndOpenLead(lead.lead_id);
  };

  const handleDeleteLead = async (leadId) => {
    try {
      await axios.delete(`${API}/leads/${leadId}`, {
        withCredentials: true,
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      });
      toast.success("Lead deleted");
      fetchLeads();
    } catch (error) {
      toast.error("Failed to delete lead");
    }
  };

  const confirmDeleteLead = (leadId) => {
    setDeleteConfirm({ open: true, leadId });
  };

  const handleAssignLead = async (leadId, userId) => {
    try {
      await axios.put(`${API}/leads/${leadId}/assign`, 
        { user_id: userId },
        {
          withCredentials: true,
          headers: token ? { Authorization: `Bearer ${token}` } : {}
        }
      );
      toast.success("Lead reassigned");
      fetchLeads();
    } catch (error) {
      toast.error("Failed to reassign lead");
    }
  };

  const handleSaveLead = async (leadData, shouldClose = false) => {
    setLeadSaveError("");
    try {
      if (selectedLead) {
        const response = await axios.put(`${API}/leads/${selectedLead.lead_id}`, leadData, {
          withCredentials: true,
          headers: token ? { Authorization: `Bearer ${token}` } : {}
        });
        toast.success("Lead saved");
        // Update selectedLead with response data to refresh modal immediately
        if (!shouldClose && response.data) {
          setSelectedLead(response.data);
        }
      } else {
        await axios.post(`${API}/leads`, leadData, {
          withCredentials: true,
          headers: token ? { Authorization: `Bearer ${token}` } : {}
        });
        toast.success("Lead created");
        shouldClose = true; // Always close after creating a new lead
      }
      if (shouldClose) {
        setIsModalOpen(false);
      }
      fetchLeads();
    } catch (error) {
      const message = error.response?.data?.detail || "Failed to save lead";
      setLeadSaveError(message);
      toast.error(message);
    }
  };

  // Bulk selection handlers
  const handleSelectAll = (checked) => {
    if (checked) {
      setSelectedLeadIds(leads.map(lead => lead.lead_id));
    } else {
      setSelectedLeadIds([]);
    }
    setLastSelectedIndex(null);
  };

  const handleSelectLead = (leadId, checked, index, event) => {
    // Shift+click for range selection
    if (event?.shiftKey && lastSelectedIndex !== null && lastSelectedIndex !== index) {
      const start = Math.min(lastSelectedIndex, index);
      const end = Math.max(lastSelectedIndex, index);
      const rangeIds = leads.slice(start, end + 1).map(lead => lead.lead_id);
      
      // Add all leads in range to selection (union with existing)
      setSelectedLeadIds(prev => {
        const newSet = new Set([...prev, ...rangeIds]);
        return Array.from(newSet);
      });
    } else {
      // Normal click - toggle single checkbox
      if (checked) {
        setSelectedLeadIds(prev => [...prev, leadId]);
      } else {
        setSelectedLeadIds(prev => prev.filter(id => id !== leadId));
      }
    }
    
    // Always update last selected index on any click
    setLastSelectedIndex(index);
  };

  const handleBulkDelete = async () => {
    if (selectedLeadIds.length === 0) return;
    
    try {
      await axios.delete(`${API}/leads/bulk`, {
        data: { lead_ids: selectedLeadIds },
        withCredentials: true,
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      });
      toast.success(`Deleted ${selectedLeadIds.length} leads`);
      setSelectedLeadIds([]);
      fetchLeads();
    } catch (error) {
      toast.error("Failed to delete leads");
    }
  };

  const confirmBulkDelete = () => {
    if (selectedLeadIds.length === 0) return;
    setBulkDeleteConfirm(true);
  };

  const handleBulkAssign = async () => {
    if (bulkAssignUserIds.length === 0 || selectedLeadIds.length === 0) return;

    try {
      await axios.put(`${API}/leads/bulk/assign`,
        { lead_ids: selectedLeadIds, user_ids: bulkAssignUserIds },
        {
          withCredentials: true,
          headers: token ? { Authorization: `Bearer ${token}` } : {}
        }
      );
      const assignedNames = salesReps
        .filter((r) => bulkAssignUserIds.includes(r.user_id))
        .map((r) => r.name)
        .join(", ");
      toast.success(`Assigned ${selectedLeadIds.length} lead${selectedLeadIds.length === 1 ? "" : "s"} to ${assignedNames}`);
      setSelectedLeadIds([]);
      setIsAssignModalOpen(false);
      setBulkAssignUserIds([]);
      fetchLeads();
    } catch (error) {
      toast.error("Failed to assign leads");
    }
  };

  const handleExport = async () => {
    try {
      const response = await axios.get(`${API}/leads/export/csv`, {
        withCredentials: true,
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'leads_export.csv');
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success("Leads exported");
    } catch (error) {
      toast.error("Failed to export leads");
    }
  };

  const handleImportFile = async (file) => {
    if (!file) return;
    
    const fileExtension = file.name.split('.').pop().toLowerCase();
    
    if (!['csv', 'xlsx', 'xls'].includes(fileExtension)) {
      toast.error("Please upload a CSV or Excel file (.csv, .xlsx)");
      return;
    }

    setImportLoading(true);
    const formData = new FormData();
    formData.append('file', file);

    try {
      const endpoint = fileExtension === 'csv' 
        ? `${API}/leads/import/csv` 
        : `${API}/leads/import/excel`;
      
      const response = await axios.post(endpoint, formData, {
        withCredentials: true,
        headers: { 
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
          'Content-Type': 'multipart/form-data'
        }
      });
      
      // Build success message
      let message = `Imported ${response.data.imported} leads`;
      if (response.data.skipped_duplicates > 0) {
        message += `, skipped ${response.data.skipped_duplicates} duplicates`;
      }
      toast.success(message);
      
      // Show duplicate names if any
      if (response.data.duplicate_names && response.data.duplicate_names.length > 0) {
        const names = response.data.duplicate_names.slice(0, 5).join(", ");
        const more = response.data.skipped_duplicates > 5 ? ` and ${response.data.skipped_duplicates - 5} more` : "";
        toast.info(`Skipped: ${names}${more}`, { duration: 6000 });
      }
      
      if (response.data.errors && response.data.errors.length > 0) {
        toast.warning(`${response.data.errors.length} rows had errors`);
      }
      setIsImportModalOpen(false);
      fetchLeads();
      if (user?.role === "manager") {
        fetchUploads();
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to import leads");
    } finally {
      setImportLoading(false);
    }
  };

  // Drag and drop handlers
  const handleDragOver = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  }, []);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    
    const files = e.dataTransfer.files;
    if (files && files.length > 0) {
      handleImportFile(files[0]);
    }
  }, [token]);

  const handleFileSelect = (event) => {
    const file = event.target.files[0];
    if (file) {
      handleImportFile(file);
    }
    event.target.value = '';
  };

  const isAllSelected = leads.length > 0 && selectedLeadIds.length === leads.length;
  const isSomeSelected = selectedLeadIds.length > 0 && selectedLeadIds.length < leads.length;

  return (
    <div className="min-h-screen bg-slate-50" data-testid="leads-page">
      <Sidebar user={user} onLogout={handleLogout} />
      
      <main className="ml-64 p-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-8 animate-fade-in">
          <div>
            <h1 
              className="text-3xl font-bold text-slate-900"
              style={{ fontFamily: 'Manrope, sans-serif' }}
            >
              Leads
            </h1>
            <p className="text-slate-500 mt-1">
              {user?.role === "manager" ? "All team leads" : "Your assigned leads"}
            </p>
          </div>
          <div className="flex items-center gap-3">
            {user?.role === "manager" && (
              <>
                <Button
                  variant="outline"
                  onClick={handleExport}
                  className="border-slate-200"
                  data-testid="export-btn"
                >
                  <Download className="w-4 h-4 mr-2" />
                  Export
                </Button>
                <Button
                  variant="outline"
                  onClick={() => setIsImportModalOpen(true)}
                  className="border-slate-200"
                  data-testid="import-btn"
                >
                  <Upload className="w-4 h-4 mr-2" />
                  Import
                </Button>
              </>
            )}
            <Button
              onClick={handleCreateLead}
              className="bg-indigo-600 hover:bg-indigo-700 text-white"
              data-testid="add-lead-btn"
            >
              <Plus className="w-4 h-4 mr-2" />
              Add Lead
            </Button>
          </div>
        </div>

        {/* Bulk Actions Bar */}
        {selectedLeadIds.length > 0 && (
          <div className="mb-4 p-4 bg-indigo-50 border border-indigo-200 rounded-lg flex items-center justify-between animate-fade-in">
            <div className="flex items-center gap-3">
              <span className="text-sm font-medium text-indigo-700">
                {selectedLeadIds.length} lead{selectedLeadIds.length > 1 ? 's' : ''} selected
              </span>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setSelectedLeadIds([])}
                className="text-indigo-600 hover:text-indigo-700 hover:bg-indigo-100"
              >
                <X className="w-4 h-4 mr-1" />
                Clear
              </Button>
            </div>
            <div className="flex items-center gap-2">
              {user?.role === "manager" && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setIsAssignModalOpen(true)}
                  className="border-indigo-200 text-indigo-700 hover:bg-indigo-100"
                  data-testid="bulk-assign-btn"
                >
                  <UserPlus className="w-4 h-4 mr-2" />
                  Assign to Rep
                </Button>
              )}
              {user?.role === "manager" && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={confirmBulkDelete}
                  className="border-red-200 text-red-600 hover:bg-red-50"
                  data-testid="bulk-delete-btn"
                >
                  <Trash2 className="w-4 h-4 mr-2" />
                  Delete Selected
                </Button>
              )}
            </div>
          </div>
        )}

        {/* Filters */}
        <Card className="border-slate-100 shadow-sm mb-6 animate-fade-in stagger-1 overflow-visible">
          <CardContent className="p-4 overflow-visible">
            <div className="flex items-center gap-4 flex-wrap">
              <div className="relative flex-1 max-w-md min-w-[200px]">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input
                  placeholder="Search leads..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="pl-10 pr-8"
                  data-testid="search-input"
                />
                {search && (
                  <button
                    onClick={() => setSearch("")}
                    className="absolute right-2 top-1/2 -translate-y-1/2 p-1 rounded-full hover:bg-slate-100 text-slate-400 hover:text-slate-600"
                    data-testid="clear-search-btn"
                  >
                    <X className="w-4 h-4" />
                  </button>
                )}
              </div>
              <MultiSelectDropdown
                value={statusFilter}
                onChange={setStatusFilter}
                options={statuses}
                showAllOption={true}
                allOptionLabel="All Statuses"
                className="w-44"
                usePortal={true}
              />
              <MultiSelectDropdown
                value={categoryFilter}
                onChange={setCategoryFilter}
                options={categories}
                showAllOption={true}
                allOptionLabel="All Categories"
                className="w-44"
                usePortal={true}
              />
              {/* Phone-presence filter — multi-select. Filter values map to
                  the backend's phone_filter CSV (mobile_has, mobile_blank,
                  phone_has, phone_blank). */}
              <MultiSelectDropdown
                value={phoneFilter}
                onChange={setPhoneFilter}
                options={phoneOptions.map((o) => o.value)}
                optionLabels={phoneOptions.reduce((acc, o) => ({ ...acc, [o.value]: o.label }), {})}
                showAllOption={true}
                allOptionLabel="All Numbers"
                className="w-44"
                usePortal={true}
                data-testid="phone-filter"
              />
              {/* Team member filter - Manager only */}
              {user?.role === "manager" && salesReps.length > 0 && (
                <Select value={teamMemberFilter} onValueChange={setTeamMemberFilter}>
                  <SelectTrigger className="w-44" data-testid="team-member-filter">
                    <Users className="w-4 h-4 mr-2 text-slate-400" />
                    <SelectValue placeholder="Filter by rep" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Team Members</SelectItem>
                    {salesReps.map((rep) => (
                      <SelectItem key={rep.user_id} value={rep.user_id}>
                        {rep.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
              {/* Upload filter - Manager only */}
              {user?.role === "manager" && uploads.length > 0 && (
                <Select value={uploadFilter} onValueChange={setUploadFilter}>
                  <SelectTrigger className="w-52" data-testid="upload-filter">
                    <FileSpreadsheet className="w-4 h-4 mr-2 text-slate-400" />
                    <SelectValue placeholder="Filter by upload" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Uploads</SelectItem>
                    {uploads.map((upload) => (
                      <SelectItem key={upload.upload_id} value={upload.upload_id}>
                        <div className="flex items-center gap-2">
                          <span className="truncate max-w-[150px]">{upload.filename}</span>
                          <span className="text-xs text-slate-400">
                            ({new Date(upload.uploaded_at).toLocaleDateString()})
                          </span>
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Leads Table */}
        <Card className="border-slate-100 shadow-sm animate-fade-in stagger-2">
          <CardContent className="p-0">
            {loading ? (
              <div className="p-8 text-center">
                <div className="spinner mx-auto"></div>
              </div>
            ) : leads.length === 0 ? (
              <div className="p-8 text-center text-slate-500">
                <p>No leads found. Create your first lead to get started!</p>
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow className="bg-slate-50/50">
                    <TableHead className="w-12">
                      <Checkbox
                        checked={isAllSelected}
                        onCheckedChange={handleSelectAll}
                        aria-label="Select all"
                        className={isSomeSelected ? "data-[state=checked]:bg-indigo-600" : ""}
                        data-testid="select-all-checkbox"
                      />
                    </TableHead>
                    <TableHead className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Company</TableHead>
                    <TableHead className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Category</TableHead>
                    <TableHead className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Contact</TableHead>
                    <TableHead className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Status</TableHead>
                    <TableHead className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Activity</TableHead>
                    {user?.role === "manager" && (
                      <TableHead className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Assigned To</TableHead>
                    )}
                    <TableHead className="w-12"></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {leads.map((lead, index) => (
                    <TableRow 
                      key={lead.lead_id} 
                      className={`table-row-hover cursor-pointer ${
                        selectedLeadIds.includes(lead.lead_id) ? 'bg-indigo-50' : ''
                      }`}
                      data-testid={`lead-row-${lead.lead_id}`}
                    >
                      <TableCell 
                        onClick={(e) => {
                          e.stopPropagation();
                          const newChecked = !selectedLeadIds.includes(lead.lead_id);
                          handleSelectLead(lead.lead_id, newChecked, index, e);
                        }}
                        className="cursor-pointer"
                      >
                        <Checkbox
                          checked={selectedLeadIds.includes(lead.lead_id)}
                          aria-label={`Select ${lead.company_name}`}
                          data-testid={`select-lead-${lead.lead_id}`}
                        />
                      </TableCell>
                      <TableCell onClick={() => handleEditLead(lead)}>
                        <div>
                          <p className="font-medium text-slate-900">{lead.company_name}</p>
                          <p className="text-sm text-slate-500">{lead.title}</p>
                        </div>
                      </TableCell>
                      <TableCell onClick={() => handleEditLead(lead)}>
                        <div className="grid grid-cols-2 gap-1 max-w-[160px]">
                          {(Array.isArray(lead.category) ? lead.category : (lead.category ? [lead.category] : [])).length > 0 ? (
                            (Array.isArray(lead.category) ? lead.category : [lead.category]).map((cat, idx) => (
                              <span 
                                key={idx}
                                className="inline-flex items-center justify-center px-1.5 py-0.5 rounded text-xs font-medium bg-slate-100 text-slate-600 truncate"
                              >
                                {cat}
                              </span>
                            ))
                          ) : (
                            <span className="text-xs text-slate-400">—</span>
                          )}
                        </div>
                      </TableCell>
                      <TableCell onClick={() => handleEditLead(lead)}>
                        <div>
                          <p className="text-slate-900">{lead.first_name} {lead.last_name}</p>
                          <p className="text-sm text-slate-500">{lead.email}</p>
                        </div>
                      </TableCell>
                      <TableCell onClick={() => handleEditLead(lead)}>
                        <Badge className={statusColors[lead.status] || "bg-slate-100 text-slate-700"}>
                          {lead.status}
                        </Badge>
                      </TableCell>
                      <TableCell onClick={() => handleEditLead(lead)}>
                        <div className="flex items-center gap-3">
                          {/* Calls */}
                          <div className="flex items-center gap-1" title="Calls made">
                            <Phone className={`w-4 h-4 ${(lead.call_logs?.length || 0) > 0 ? 'text-emerald-500' : 'text-slate-300'}`} />
                            <span className={`text-xs font-medium ${(lead.call_logs?.length || 0) > 0 ? 'text-emerald-600' : 'text-slate-400'}`}>
                              {lead.call_logs?.length || 0}
                            </span>
                          </div>
                          {/* Scheduled Calls */}
                          <div className="flex items-center gap-1" title="Scheduled calls">
                            <PhoneOutgoing className={`w-4 h-4 ${(lead.scheduled_calls?.length || 0) > 0 ? 'text-blue-500' : 'text-slate-300'}`} />
                            <span className={`text-xs font-medium ${(lead.scheduled_calls?.length || 0) > 0 ? 'text-blue-600' : 'text-slate-400'}`}>
                              {lead.scheduled_calls?.length || 0}
                            </span>
                          </div>
                          {/* Text Messages */}
                          <div className="flex items-center gap-1" title="Text messages">
                            <MessageSquare className={`w-4 h-4 ${(lead.text_logs?.length || 0) > 0 ? 'text-purple-500' : 'text-slate-300'}`} />
                            <span className={`text-xs font-medium ${(lead.text_logs?.length || 0) > 0 ? 'text-purple-600' : 'text-slate-400'}`}>
                              {lead.text_logs?.length || 0}
                            </span>
                          </div>
                          {/* Meetings */}
                          <div className="flex items-center gap-1" title="Meetings">
                            <Calendar className={`w-4 h-4 ${(lead.meetings?.length || 0) > 0 ? 'text-amber-500' : 'text-slate-300'}`} />
                            <span className={`text-xs font-medium ${(lead.meetings?.length || 0) > 0 ? 'text-amber-600' : 'text-slate-400'}`}>
                              {lead.meetings?.length || 0}
                            </span>
                          </div>
                          {/* Comments */}
                          <div className="flex items-center gap-1" title="Comments">
                            <MessageCircle className={`w-4 h-4 ${(lead.comments?.length || 0) > 0 ? 'text-indigo-500' : 'text-slate-300'}`} />
                            <span className={`text-xs font-medium ${(lead.comments?.length || 0) > 0 ? 'text-indigo-600' : 'text-slate-400'}`}>
                              {lead.comments?.length || 0}
                            </span>
                          </div>
                        </div>
                      </TableCell>
                      {user?.role === "manager" && (
                        <TableCell>
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
                              <Button variant="ghost" size="sm" className="h-8 px-2">
                                <span className="text-sm text-slate-600">
                                  {(() => {
                                    // Display assigned reps in a compact form. Multi-assign
                                    // stores assigned_to_names as an array; legacy rows
                                    // have a single assigned_to_name string. Show the
                                    // first name + "+N more" when there are multiple.
                                    const names = Array.isArray(lead.assigned_to_names)
                                      ? lead.assigned_to_names
                                      : (lead.assigned_to_name ? [lead.assigned_to_name] : []);
                                    if (names.length === 0) return "Unassigned";
                                    if (names.length === 1) return names[0];
                                    return `${names[0]} +${names.length - 1}`;
                                  })()}
                                </span>
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="start">
                              {salesReps.map((rep) => (
                                <DropdownMenuItem 
                                  key={rep.user_id}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleAssignLead(lead.lead_id, rep.user_id);
                                  }}
                                >
                                  <UserPlus className="w-4 h-4 mr-2" />
                                  {rep.name}
                                </DropdownMenuItem>
                              ))}
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </TableCell>
                      )}
                      <TableCell>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
                            <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
                              <MoreHorizontal className="w-4 h-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem onClick={(e) => {
                              e.stopPropagation();
                              handleEditLead(lead);
                            }}>
                              <Edit className="w-4 h-4 mr-2" />
                              Edit
                            </DropdownMenuItem>
                            {user?.role === "manager" && (
                              <DropdownMenuItem 
                                onClick={(e) => {
                                  e.stopPropagation();
                                  confirmDeleteLead(lead.lead_id);
                                }}
                                className="text-red-600"
                              >
                                <Trash2 className="w-4 h-4 mr-2" />
                                Delete
                              </DropdownMenuItem>
                            )}
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
      </main>

      {/* Lead Modal */}
      <LeadModal
        isOpen={isModalOpen}
        onClose={() => {
          ++openRequest.current;
          setIsModalOpen(false);
          setInitialModalTab(null); // Reset initial tab when closing
        }}
        lead={selectedLead}
        onSave={handleSaveLead}
        saveError={leadSaveError}
        teamMembers={salesReps}
        userRole={user?.role}
        onActivityUpdate={fetchLeads}
        leads={leads}
        onNavigate={handleEditLead}
        initialTab={initialModalTab}
      />

      {/* Bulk Assign Modal — multi-assign: pick one or more reps */}
      <Dialog open={isAssignModalOpen} onOpenChange={setIsAssignModalOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Manrope, sans-serif' }}>
              Assign {selectedLeadIds.length} Lead{selectedLeadIds.length > 1 ? 's' : ''}
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4">
            <p className="text-sm text-slate-600">
              Pick one or more team members. All selected reps will share ownership — each sees the lead in their personal feed.
            </p>

            <MultiSelectDropdown
              value={bulkAssignUserIds}
              onChange={setBulkAssignUserIds}
              options={salesReps.map((r) => r.user_id)}
              optionLabels={salesReps.reduce((acc, r) => ({ ...acc, [r.user_id]: r.name }), {})}
              placeholder="Select team members"
              className="w-full"
              data-testid="bulk-assign-select"
            />

            <div className="flex justify-end gap-2 pt-4">
              <Button
                variant="outline"
                onClick={() => {
                  setIsAssignModalOpen(false);
                  setBulkAssignUserIds([]);
                }}
              >
                Cancel
              </Button>
              <Button
                onClick={handleBulkAssign}
                disabled={bulkAssignUserIds.length === 0}
                className="bg-indigo-600 hover:bg-indigo-700 text-white"
                data-testid="confirm-bulk-assign-btn"
              >
                Assign Leads
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Import Modal with Drag & Drop */}
      <Dialog open={isImportModalOpen} onOpenChange={setIsImportModalOpen}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Manrope, sans-serif' }}>
              Import Leads
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            <p className="text-sm text-slate-600">
              Upload a CSV or Excel file (.xlsx) with your leads. The file should include columns like: 
              Company name, First name, Last name, Email, Phone number, Status, etc.
            </p>
            
            {/* Download Template Button */}
            <div className="flex justify-center">
              <Button
                variant="outline"
                onClick={() => {
                  window.open(`${API}/leads/template`, '_blank');
                }}
                className="border-indigo-200 text-indigo-600 hover:bg-indigo-50"
                data-testid="download-leads-template-btn"
              >
                <Download className="w-4 h-4 mr-2" />
                Download Import Template
              </Button>
            </div>
            
            {/* Drag & Drop Zone */}
            <div
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
              className={`
                relative border-2 border-dashed rounded-lg p-8 text-center transition-all
                ${isDragging 
                  ? 'border-indigo-500 bg-indigo-50' 
                  : 'border-slate-200 hover:border-slate-300 bg-slate-50'
                }
                ${importLoading ? 'opacity-50 pointer-events-none' : ''}
              `}
              data-testid="import-dropzone"
            >
              {importLoading ? (
                <div className="flex flex-col items-center gap-3">
                  <div className="spinner w-8 h-8 border-4 border-slate-200 border-t-indigo-600 rounded-full"></div>
                  <p className="text-slate-600">Importing leads...</p>
                </div>
              ) : (
                <>
                  <FileSpreadsheet className={`w-12 h-12 mx-auto mb-4 ${isDragging ? 'text-indigo-500' : 'text-slate-400'}`} />
                  <p className="text-slate-700 font-medium mb-1">
                    {isDragging ? 'Drop file here' : 'Drag & drop your file here'}
                  </p>
                  <p className="text-sm text-slate-500 mb-4">or</p>
                  <label>
                    <input
                      type="file"
                      accept=".csv,.xlsx,.xls"
                      onChange={handleFileSelect}
                      className="hidden"
                      data-testid="import-file-input"
                    />
                    <Button
                      type="button"
                      variant="outline"
                      className="border-slate-200"
                      asChild
                    >
                      <span>
                        <Upload className="w-4 h-4 mr-2" />
                        Browse Files
                      </span>
                    </Button>
                  </label>
                  <p className="text-xs text-slate-400 mt-4">
                    Supported formats: .csv, .xlsx
                  </p>
                </>
              )}
            </div>

            {/* Sample format info */}
            <div className="p-3 rounded-lg bg-slate-100 text-xs text-slate-600">
              <p className="font-medium mb-1">Expected columns:</p>
              <p>Company name, First name, Last name, Title, Phone number, Email, Status, Called, Did they answer, Are they interested, Meeting date and time, Caller notes</p>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Delete Lead Confirmation */}
      <ConfirmDialog
        open={deleteConfirm.open}
        onOpenChange={(open) => setDeleteConfirm({ open, leadId: null })}
        title="Delete Lead?"
        description="Are you sure you want to delete this lead? This action cannot be undone."
        confirmText="Delete"
        variant="destructive"
        onConfirm={() => handleDeleteLead(deleteConfirm.leadId)}
      />

      {/* Bulk Delete Confirmation */}
      <ConfirmDialog
        open={bulkDeleteConfirm}
        onOpenChange={setBulkDeleteConfirm}
        title={`Delete ${selectedLeadIds.length} Lead${selectedLeadIds.length > 1 ? 's' : ''}?`}
        description={`Are you sure you want to delete ${selectedLeadIds.length} selected lead${selectedLeadIds.length > 1 ? 's' : ''}? This action cannot be undone.`}
        confirmText="Delete"
        variant="destructive"
        onConfirm={handleBulkDelete}
      />
    </div>
  );
};

export default Leads;
